// War Room "Buy Box sources" (owner 2026-10-25): the three Buy Box sources side by side so their
// differences can be compared. Amazon = Business Report, page-view weighted, 28 days (measured over
// real shoppers). Regional crawler = consumer Buy Box in 50 ZIPs, 35 days. Offers snapshot = the
// business-buyer Buy Box from one default location (getItemOffers CustomerType=Business), 35 days.
// The War Room verdicts still use the snapshot until the owner decides otherwise.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ApiClient from '../../utils/ApiClient';
import WarRoomTabs from './WarRoomTabs';

const DASH = '—';
const pct = (v) => (v === null || v === undefined ? DASH : `${Number(v).toFixed(1)}%`);
const money = (v) => (v === null || v === undefined ? DASH : `$${Number(v).toFixed(2)}`);

function holderText(src, withZips) {
    if (!src || !src.holderId) return DASH;
    const zips = withZips && src.holderZips != null ? ` · ${src.holderZips}/${src.zipsChecked ?? '?'} ZIPs` : '';
    return `${src.holderName || src.holderId}${zips}`;
}

function Cell({ children, testid, className = '' }) {
    return <td data-testid={testid} className={`px-2 py-1.5 text-right whitespace-nowrap ${className}`}>{children}</td>;
}

export default function BuyBoxCompare() {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [disagreeOnly, setDisagreeOnly] = useState(true);
    const [search, setSearch] = useState('');

    const load = useCallback(async (refresh) => {
        setLoading(true);
        setError(null);
        try {
            const res = await ApiClient.get(`/api/war-room/bb-compare${refresh ? '?refresh=true' : ''}`);
            setData(res.data);
        } catch (e) {
            setError('Could not load the Buy Box comparison: ' + e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(false); }, [load]);

    const rows = useMemo(() => {
        if (!data) return [];
        const q = search.trim().toLowerCase();
        return data.rows.filter(r => (!disagreeOnly || r.disagree)
            && (!q || r.asin.toLowerCase().includes(q) || (r.title || '').toLowerCase().includes(q)));
    }, [data, disagreeOnly, search]);

    const s = data?.summary;
    return (
        <div className="p-4 space-y-4">
            <WarRoomTabs active="Buy Box sources" />
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                    <h1 className="text-lg font-semibold text-gray-900">Buy Box sources compared</h1>
                    <p className="text-xs text-gray-500 max-w-3xl">
                        <b>Amazon</b>: Business Report Buy Box %, page-view weighted, 28 days — Amazon's own measurement over real shoppers.{' '}
                        <b>Regional crawler</b>: the regular-shopper Buy Box in 50 ZIP codes, 35 days.{' '}
                        <b>Offers snapshot</b>: the <i>business-buyer</i> Buy Box from one default location, 35 days — what the War Room verdicts use today.
                        A row is flagged when the crawler and snapshot name different current holders, or our share differs by {s?.shareGapThresholdPts ?? 20}+ points.
                    </p>
                </div>
                <button type="button" onClick={() => load(true)} disabled={loading}
                        className="px-3 py-1.5 text-sm rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50">
                    {loading ? 'Loading…' : 'Refresh'}
                </button>
            </div>

            {error && <div className="p-3 rounded bg-red-50 text-sm text-red-700">{error}</div>}

            {s && (
                <div data-testid="bb-compare-summary" className="grid grid-cols-2 md:grid-cols-6 gap-2 text-sm">
                    {[
                        ['Crawler covers', `${s.regionalAsins} ASINs`],
                        ['Snapshot covers', `${s.snapshotAsins} ASINs`],
                        ['Amazon report covers', `${s.amazonAsins} ASINs`],
                        ['In both crawler + snapshot', `${s.bothAsins}`],
                        ['Different current holder', `${s.holderDisagree}`],
                        ['Flagged (holder or ≥20 pt gap)', `${s.disagree} · avg gap ${s.avgShareGapPts ?? DASH} pts`],
                    ].map(([label, value]) => (
                        <div key={label} className="bg-white border border-gray-200 rounded p-2">
                            <div className="text-xs text-gray-500">{label}</div>
                            <div className="font-semibold text-gray-900">{value}</div>
                        </div>
                    ))}
                </div>
            )}
            {s && s.unavailable && s.unavailable.length > 0 && (
                <div className="p-2 rounded bg-amber-50 text-sm text-amber-800">Unavailable: {s.unavailable.join(', ')}</div>
            )}

            <div className="flex items-center gap-4 text-sm">
                <label className="flex items-center gap-2">
                    <input type="checkbox" data-testid="bb-compare-disagree-only" checked={disagreeOnly}
                           onChange={e => setDisagreeOnly(e.target.checked)} />
                    Disagreements only
                </label>
                <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search ASIN or title…"
                       className="px-2 py-1 border border-gray-300 rounded text-sm w-64" />
                {data && <span className="text-xs text-gray-500">{rows.length} of {data.rows.length} products</span>}
            </div>

            {data && (
                <div className="overflow-x-auto bg-white border border-gray-200 rounded">
                    <table className="min-w-full text-xs">
                        <thead className="bg-gray-50 text-gray-600">
                            <tr>
                                <th className="px-2 py-1" colSpan={2}></th>
                                <th className="px-2 py-1 border-l border-gray-200 text-center">Amazon</th>
                                <th className="px-2 py-1 border-l border-gray-200 text-center bg-blue-50" colSpan={4}>Regional crawler (shoppers, 50 ZIPs)</th>
                                <th className="px-2 py-1 border-l border-gray-200 text-center bg-purple-50" colSpan={4}>Offers snapshot (business buyer)</th>
                                <th className="px-2 py-1 border-l border-gray-200" colSpan={2}></th>
                            </tr>
                            <tr>
                                <th className="px-2 py-1 text-left">ASIN</th>
                                <th className="px-2 py-1 text-left">Title</th>
                                <th className="px-2 py-1 text-right border-l border-gray-200">Our BB% 28d</th>
                                <th className="px-2 py-1 text-right border-l border-gray-200 bg-blue-50">Holder now</th>
                                <th className="px-2 py-1 text-right bg-blue-50">Price</th>
                                <th className="px-2 py-1 text-right bg-blue-50">Our share</th>
                                <th className="px-2 py-1 text-right bg-blue-50">MP share</th>
                                <th className="px-2 py-1 text-right border-l border-gray-200 bg-purple-50">Holder now</th>
                                <th className="px-2 py-1 text-right bg-purple-50">Price</th>
                                <th className="px-2 py-1 text-right bg-purple-50">Our share</th>
                                <th className="px-2 py-1 text-right bg-purple-50">MP share</th>
                                <th className="px-2 py-1 text-right border-l border-gray-200">Our share gap</th>
                                <th className="px-2 py-1 text-left">Flag</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {rows.map(r => {
                                const reg = r.regional, snap = r.snapshot;
                                return (
                                    <tr key={r.asin} data-testid={`bb-compare-row-${r.asin}`} className={r.disagree ? 'bg-amber-50' : ''}>
                                        <td className="px-2 py-1.5 font-mono">{r.asin}</td>
                                        <td className="px-2 py-1.5 max-w-xs whitespace-normal">{r.title || DASH}</td>
                                        <Cell className="border-l border-gray-200">{pct(r.amazonBbPct)}</Cell>
                                        <Cell testid="regional-cell" className="border-l border-gray-200">{holderText(reg, true)}</Cell>
                                        <Cell testid="regional-cell">{money(reg?.holderPrice)}</Cell>
                                        <Cell testid="regional-cell">{pct(reg?.ourSharePct)}</Cell>
                                        <Cell testid="regional-cell">{pct(reg?.mpSharePct)}</Cell>
                                        <Cell testid="snapshot-cell" className="border-l border-gray-200">{holderText(snap, false)}</Cell>
                                        <Cell testid="snapshot-cell">{money(snap?.holderPrice)}</Cell>
                                        <Cell testid="snapshot-cell">{pct(snap?.ourSharePct)}</Cell>
                                        <Cell testid="snapshot-cell">{pct(snap?.mpSharePct)}</Cell>
                                        <Cell className="border-l border-gray-200 font-semibold">
                                            {r.shareGapPts === null || r.shareGapPts === undefined ? DASH : `${Number(r.shareGapPts).toFixed(1)} pts`}
                                        </Cell>
                                        <td className="px-2 py-1.5 whitespace-nowrap">
                                            {r.holderDisagree ? 'Different holder' : r.disagree ? 'Share gap' : ''}
                                        </td>
                                    </tr>
                                );
                            })}
                            {rows.length === 0 && (
                                <tr><td colSpan={13} className="px-2 py-6 text-center text-gray-500">No products match.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
